/**
 * EmailLinkedLeadPanel — right-rail panel on the Email page that mirrors
 * the chat right-panel for the lead linked to a Zoho ticket.
 *
 * Originally Iter 145 (Feb 2026) — single 455-line file. Split in Iter
 * 156.9 (Feb 28, 2026) into:
 *
 *   • `useEmailLinkedLead.js`         — all state + handlers (custom hook)
 *   • `ZohoTicketStatusSection.jsx`   — the 4-state status picker
 *   • (this file)                     — pure JSX composition + tabs
 *
 * Same default export so EmailsPage.jsx doesn't need to change.
 */
/* eslint-disable react/prop-types */
import { useState } from "react";
import { Loader2, MessageSquare, RefreshCw, X } from "lucide-react";

import { InfoTab, OrderTab, AiTab } from "../chat/ChatRightPanel";
import { ProductLookupTab } from "../chat/ProductLookupTab";

import ZohoTicketStatusSection from "./ZohoTicketStatusSection";
import useEmailLinkedLead from "./useEmailLinkedLead";

const TABS = [
  { k: "info", label: "Info" },
  { k: "order", label: "Order" },
  { k: "product", label: "Product" },
  { k: "ai", label: "AI" },
];

export default function EmailLinkedLeadPanel({
  leadId,
  // Iter 146 — Zoho status controls replace the WATI status section.
  ticketStatus,
  onTicketStatusChange,
  statusLoading = false,
  onClose,
}) {
  const [tab, setTab] = useState("info");
  const h = useEmailLinkedLead(leadId);

  if (h.loading || !h.lead) {
    return (
      <aside
        className="w-[340px] flex-shrink-0 border-l border-[--border] bg-white flex items-center justify-center"
        data-testid="email-linked-lead-panel-loading"
      >
        <Loader2 size={20} className="animate-spin text-[--text-muted]" />
      </aside>
    );
  }

  return (
    <aside
      className="w-[340px] flex-shrink-0 border-l border-[--border] bg-white flex flex-col"
      data-testid="email-linked-lead-panel"
    >
      {/* Header strip — agent identity + open-in-chats link + close */}
      <div className="flex-shrink-0 border-b border-[--border] px-3 py-2.5 flex items-center justify-between bg-gradient-to-r from-[--bg-cream] to-white">
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">Linked WATI Lead</p>
          <p className="text-xs font-bold text-[--text-primary] truncate" data-testid="elp-lead-name">
            {h.lead.name || h.lead.phone}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <a
            href={`/chats?lead=${h.lead.id}`}
            className="text-[10px] font-bold px-2 py-1 rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 inline-flex items-center gap-1"
            title="Open this lead's full chat (send WhatsApp messages, etc.)"
            data-testid="elp-open-in-chats"
          >
            <MessageSquare size={10} /> Chat
          </a>
          <button onClick={h.refresh} className="text-[--text-muted] hover:text-[--text-primary] p-1" title="Refresh" data-testid="elp-refresh">
            <RefreshCw size={12} />
          </button>
          {onClose && (
            <button onClick={onClose} className="text-[--text-muted] hover:text-[--text-primary] p-1" title="Close panel" data-testid="elp-close">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 flex border-b border-[--border] bg-gray-50/60">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex-1 text-[11px] font-bold py-2 transition-colors ${
              tab === t.k ? "text-[--accent] border-b-2 border-[--accent] bg-white" : "text-[--text-muted] hover:text-[--text-primary]"
            }`}
            data-testid={`elp-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-3" data-testid={`elp-content-${tab}`}>
        {tab === "info" && (
          <InfoTab
            lead={h.lead}
            agents={[]}
            user={{}}
            onReassign={() => {}}
            onWatiStatus={() => {}}
            onSetDepartment={() => {}}
            allLabels={h.allLabels}
            onAddLabel={h.onAddLabel}
            onRemoveLabel={h.onRemoveLabel}
            newLabelText={h.newLabelText}
            setNewLabelText={h.setNewLabelText}
            showLabelInput={h.showLabelInput}
            setShowLabelInput={h.setShowLabelInput}
            hideWatiStatus
            extraStatusSlot={
              onTicketStatusChange ? (
                <ZohoTicketStatusSection
                  currentStatus={ticketStatus}
                  onChange={onTicketStatusChange}
                  loading={statusLoading}
                />
              ) : null
            }
          />
        )}
        {tab === "order" && (
          <OrderTab
            lead={h.lead}
            custForm={h.custForm}
            setCustForm={h.setCustForm}
            savingCust={h.savingCust}
            onSave={h.onSaveCust}
            onPlaceOrder={h.onPlaceOrder}
            onAiExtract={h.runExtract}
            aiLoading={h.aiLoading}
            orderNum={h.orderNum}
            setOrderNum={h.setOrderNum}
            orderAmt={h.orderAmt}
            setOrderAmt={h.setOrderAmt}
            onRecordOrder={h.onRecordOrder}
          />
        )}
        {tab === "product" && (
          <ProductLookupTab
            priceScope={h.priceScope}
            detectedProducts={[]}
            productLookup={h.productLookup}
            setProductLookup={h.setProductLookup}
            productLoading={h.productLoading}
            productData={h.productData}
            setProductData={h.setProductData}
            productSuggestions={h.productSuggestions}
            setProductSuggestions={h.setProductSuggestions}
            lookupProduct={h.lookupProduct}
            selectedLead={h.lead}
            buildProductDetailsMessage={h.buildProductDetailsMessage}
            copyProductSummary={h.copyProductSummary}
            // Email surface has no WhatsApp send — wire "send" to "copy".
            sendProductSummary={h.copyProductSummary}
          />
        )}
        {tab === "ai" && (
          <AiTab
            lead={h.lead}
            onSuggest={h.runSuggest}
            onExtract={h.runExtract}
            onSentiment={h.runSentiment}
            onCategorize={h.runCategorize}
            aiLoading={h.aiLoading}
          />
        )}
      </div>
    </aside>
  );
}
